import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter, firstValueFrom, map, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { captureException } from '@sentry/angular';
import { percentage } from '@angular/fire/storage';
import { environment } from 'src/environments/environment';
import { BoardItemsManager } from '../sessions/session/items-canvas/board-items/board-items-manager';
import type { BoardItem } from '../sessions/session/items-canvas/board-items/board.item';
import { ERRORS, INFOS } from '../common/utils/notification-constants';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { IconMessageToasterElement } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { UPLOAD_LIMITS } from '../dialogs/upload/upload/upload.component';
import { SpaceRepository } from '../state/space.repository';
import { ResourceItemModel } from '../models/resource';
import {
  ButtonToasterElement,
  ButtonToasterElementStyle,
} from '../ui/notification-toaster/button-toaster-element/button-toaster-element.component';
import { PanelView } from '../sessions/panel/panel.component';
import { ItemModel } from '../models/itemModel';
import {
  IMAGES_DEFAULT_HEIGHT,
  IMAGES_DEFAULT_WIDTH,
  VIDEO_DEFAULT_HEIGHT,
  VIDEO_DEFAULT_WIDTH,
} from '../sessions/session/iframe/additional-apps.utils';
import { getFileFromUrl } from '../utilities/FileUtils';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { Frame } from '../models/session';
import {
  BulkUploadService,
  BulkUploadStep,
} from '../sessions/session/upload/bulk-upload-panel/services/bulk-upload.service';
import { SessionsVptService } from './sessions-vpt.service';
import { DomListenerFactoryService } from './dom-listener-factory.service';
import { PDFConverterService } from './pdfconverter.service';
import {
  NotificationDataBuilder,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
} from './notification-toaster.service';
import { SpaceBoardsService } from './space-boards.service';
import { SessionSharedDataService } from './session-shared-data.service';
import {
  checkIfValidLibreOfficeType,
  GroupUploadData,
  resolveDocumentMimeType,
  UploadFailedNotificationStatus,
  UploadFileService,
} from './upload-file.service';
import { ImageConversionService } from './image-conversion.service';
import { imageNeedsConversion, isImageFile } from './image-file-type.utils';
import { pollHeadUntilUrlOk } from './url-head-poll';
import { ResourcesService } from './resources.service';
import { VimeoUploadService } from './vimeo-upload.service';
import { CenterPosition } from './items-canvas.service';
import { BoardCloneService } from './board-clone.service';
import { FLAGS, FlagsService } from './flags.service';
import { CdnResourcesSignerService } from './cdn-resources-signer.service';
import { ChildModeService } from './child-mode.service';
import { ObserverModeService } from './observer-mode.service';
import { FeatureManagementService } from './feature-management.service';

export type UploadOptions = Partial<{
  x: number;
  y: number;
  width: number;
  groupUploadData: GroupUploadData;
  renderInNearestFreeSpace: boolean;
}>;

// Image resize defaults
const DEFAULT_MAX_IMAGE_RELOAD_ATTEMPTS = 10;
const DEFAULT_MAX_IMAGE_WIDTH = 4000;
const DEFAULT_MAX_IMAGE_HEIGHT = 4000;
const DEFAULT_IMAGE_RELOAD_POLL_INTERVAL_MS = 1000;

export class UploadFile {
  file: File | string;
  name: string;
  size: number;
  type: ResourceItemModel;
  sessionId: string;
  frameUid: string;
  options?: UploadOptions;
  publicFile?: boolean;

  constructor(
    file: File | string,
    name: string,
    size: number,
    type: ResourceItemModel,
    sessionId: string,
    frameUid: string,
    options: UploadOptions | undefined = undefined,
    publicFile = false,
  ) {
    this.file = file;
    this.name = name;
    this.size = size;
    this.type = type;
    this.sessionId = sessionId;
    this.frameUid = frameUid;
    this.options = options;
    this.publicFile = publicFile;
  }
}

@Injectable({
  providedIn: 'root',
})
export class DocumentUploadService {
  private domListener = this.domListenerFactoryService.createInstance();
  private uploadItemSubscription?: Subscription;

  constructor(
    private resourcesService: ResourcesService,
    private pdfConverterService: PDFConverterService,
    private domListenerFactoryService: DomListenerFactoryService,
    private translate: TranslateService,
    private sessionsVptService: SessionsVptService,
    private notificationToasterService: NotificationToasterService,
    private spaceRepo: SpaceRepository,
    private spaceBoardsService: SpaceBoardsService,
    private sharedDataService: SessionSharedDataService,
    private uploadFileService: UploadFileService,
    private vimeoStorage: VimeoUploadService,
    private boardCloneService: BoardCloneService,
    private flagsService: FlagsService,
    private bulkUploadService: BulkUploadService,
    private cdnResourcesSignerService: CdnResourcesSignerService,
    private childModeService: ChildModeService,
    private imageConverterService: ImageConversionService,
    private observerModeService: ObserverModeService,
    private featureManagementService: FeatureManagementService,
  ) {}

  init() {
    this.subscribeToUploadItemObservable();
    this.addDropHandler();
  }

  public destroyService() {
    this.domListener.clear();
    this.uploadItemSubscription?.unsubscribe();
  }

  subscribeToUploadItemObservable(): void {
    this.uploadItemSubscription?.unsubscribe();
    this.uploadItemSubscription = this.sharedDataService.uploadItem
      .pipe(
        filter((files) => Boolean(files) && Array.isArray(files)),
        map((files) => (files as UploadFile[]).filter((file) => Boolean(file.file))),
        map((files) => files as UploadFile[]),
      )
      .subscribe((files: UploadFile[]) => {
        for (const file of files) {
          switch (file.type) {
            case ResourceItemModel.PDF:
            case ResourceItemModel.DOCUMENT:
              this.startPDFUploadOperations(file, file.sessionId, file.frameUid, file.options);
              break;
            case ResourceItemModel.IMAGE:
              this.startImageUploadOperations(file, file.sessionId, file.frameUid, file.options);
              break;
            case ResourceItemModel.VIDEO:
              this.startVideoUploadOperations(file, file.sessionId, file.frameUid, file.options);
              break;
          }
        }
      });
  }

  private addDropHandler(): void {
    this.domListener.add(document, 'drop', ($event) => {
      if (
        this.flagsService.isFlagEnabled(FLAGS.ENABLE_BULK_UPLOAD_FLOW) &&
        this.bulkUploadService.currentStep$.getValue() !== BulkUploadStep.Drop
      ) {
        $event.preventDefault();
        return;
      }

      if (this.childModeService.isChildModeActive) {
        $event.preventDefault();
        return;
      }

      if (this.observerModeService.isObserverModeActive()) {
        $event.preventDefault();
        this.showUploadFailedNotification(
          this.translate.instant('Observers cannot upload objects to the space'),
        );
        return;
      }

      if (this.sharedDataService.isBoardLockedByHost()) {
        $event.preventDefault();
        this.showUploadFailedNotification(
          this.translate.instant(
            'You cannot upload objects to a Space when it is locked by a host',
          ),
        );
        return;
      }

      const dropPositionOnCanvas = this.getDropPositionOnCanvas($event);
      if ($event.dataTransfer?.files.length) {
        const files = Array.from($event.dataTransfer.files || []) as File[];

        const boardFiles = files.filter((f) => this.isBoardFile(f));
        if (boardFiles.length && this.featureManagementService.isExportImportSpaceFileEnabled()) {
          this.handleDroppedBoardFiles(boardFiles, $event);
        }

        const filesWithoutBoardFiles = files.filter((f) => !this.isBoardFile(f));
        this.uploadFiles(filesWithoutBoardFiles, $event, dropPositionOnCanvas);
      } else if ($event.dataTransfer?.getData('text/uri-list')) {
        this.insertImageFromUriList($event, dropPositionOnCanvas);
      }
    });
  }

  getDropPositionOnCanvas(event: { layerX: any; layerY: any }): CenterPosition {
    // Maps the drop point from the screen to the canvas coordinates
    const boundingRect = this.sharedDataService.fabricCanvas?.vptCoords;

    // Check if we have valid canvas viewport and window dimensions
    if (boundingRect !== undefined && window.innerHeight && window.innerWidth) {
      // Extract viewport boundaries from the canvas
      const top = boundingRect.tr.y; // Top-right Y coordinate
      const bottom = boundingRect.br.y; // Bottom-right Y coordinate
      const left = boundingRect.bl.x; // Bottom-left X coordinate
      const right = boundingRect.br.x; // Bottom-right X coordinate

      // Calculate X coordinate:
      // 1. Get the drop point's X position relative to window (layerX)
      // 2. Convert it to a percentage of window width
      // 3. Map this percentage to the canvas viewport width
      const x = event.layerX;
      const relativeX = x / window.innerWidth; // Convert to 0-1 range
      const width = right - left;
      const newX = left + relativeX * width; // Map to canvas coordinates

      // Calculate Y coordinate:
      // Same process as X coordinate but for vertical position
      const y = event.layerY;
      const relativeY = y / window.innerHeight; // Convert to 0-1 range
      const height = bottom - top;
      const newY = top + relativeY * height; // Map to canvas coordinates

      return {
        y: newY,
        x: newX,
      };
    } else {
      // Fallback: If we can't calculate relative position,
      // place the item at the center of the viewport
      const centerCoords = this.sessionsVptService.getCenterCoords();
      return {
        y: centerCoords.y,
        x: centerCoords.x,
      };
    }
  }

  uploadFiles(
    files: File[],
    event: { preventDefault: () => void },
    position?: CenterPosition,
  ): void {
    if (files?.length) {
      this.addExternalFile(Array.from(files), position);
      event.preventDefault();
    }
  }

  public addExternalFile(files: File[], position?: CenterPosition): void {
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_BULK_UPLOAD_FLOW)) {
      this.bulkUploadService.addFiles(files, position);
      return;
    }
    if (!this.spaceRepo.activeSpace || !this.spaceBoardsService.activeSpaceSelectedBoard) {
      return;
    }
    const currentSessionId = this.spaceRepo.activeSpace._id;
    const currentFrameUid = this.spaceBoardsService.activeSpaceSelectedBoard.uid;

    const filesToUpload: UploadFile[] = [];

    // Show upload modal for first pdf only and ignore the rest
    if (this.uploadFileService.containsMoreThanOneType(files, 'application/pdf')) {
      files = this.uploadFileService.AllowOneFileFromType(files, 'application/pdf');
    }

    files.forEach((file) => {
      let itemModel = '';
      if (file.type.includes('pdf') || checkIfValidLibreOfficeType(file)) {
        this.handlePdfFileDrop(file);
        return;
      } else if (file.type.includes('video')) {
        itemModel = ResourceItemModel.VIDEO;
      } else if (isImageFile(file)) {
        itemModel = ResourceItemModel.IMAGE;
      }

      if (itemModel) {
        const uploadFile = new UploadFile(
          file,
          file.name,
          file.size,
          itemModel as ResourceItemModel,
          currentSessionId,
          currentFrameUid,
          position,
        );

        filesToUpload.push(uploadFile);
      } else {
        const title = new IconMessageToasterElement(
          { icon: 'cloud_upload', size: 16 },
          this.translate.instant('Upload file type error'),
        );
        const message = new IconMessageToasterElement(
          undefined,
          this.translate.instant('This file type is not yet supported'),
        );
        const okButton = new ButtonToasterElement(
          [undefined, this.translate.instant('Ok')],
          {
            handler: () => undefined,
            close: true,
          },
          ButtonToasterElementStyle.RAISED,
        );
        const uploadFailedNotificationData = new NotificationDataBuilder(ERRORS.UPLOAD_FAILED)
          .type(NotificationType.ERROR)
          .style(ToasterPopupStyle.ERROR)
          .topElements([title])
          .middleElements([message])
          .bottomElements([okButton])
          .priority(1030)
          .build();
        this.notificationToasterService.showNotification(uploadFailedNotificationData);
      }
    });

    this.sharedDataService.uploadItem.next(filesToUpload);
  }

  private isBoardFile(file: File): boolean {
    return Boolean(file?.name?.toLowerCase().endsWith(this.boardCloneService.fileExtension));
  }

  private async handleDroppedBoardFiles(files: File[], $event: any): Promise<void> {
    // preventing the default behavior of opening the file in a new tab
    $event.preventDefault();

    // If any dropped file contains a space, import only the first space file and ignore others
    for (const file of files) {
      const isSpace = await this.boardCloneService.isSpaceFile(file);
      if (isSpace) {
        await this.boardCloneService.importSpaceOrBoardFromFile(file);
        return;
      }
    }

    // Otherwise, import all and show a success notification
    let newFrame: Frame | undefined;
    for (const file of files) {
      newFrame = await this.boardCloneService.importSpaceOrBoardFromFile(file, true);
    }

    if (!newFrame) {
      return;
    }

    if (files.length === 1) {
      this.boardCloneService.notifyBoardImported(newFrame);
    } else {
      this.boardCloneService.notifyBoardsOrSpaceImported();
    }
  }

  private handlePdfFileDrop(file: File): void {
    this.uploadFileService.setPendingLeftPanelFile(file);
    if (this.sharedDataService.leftPanelView.getValue()?.panelView !== PanelView.uploadFile) {
      // only call this when the value is not already PanelView.uploadFile because if so, the panel will close
      this.sharedDataService.changeLeftPanelView.next(PanelView.uploadFile);
    }
  }

  getImageDimensions(file: File | string): Promise<{ height: number; width: number }> {
    return new Promise((resolve) => {
      const resolveWithDefaults = () =>
        resolve({ height: IMAGES_DEFAULT_HEIGHT, width: IMAGES_DEFAULT_WIDTH });

      const timeoutId = modifiedSetTimeout(resolveWithDefaults, 5000, true);

      const img = new Image();
      img.onload = () => {
        clearTimeout(timeoutId);

        return resolve(this.calculateImageDimensions(img));
      };

      img.onerror = resolveWithDefaults;

      img.src = typeof file === 'string' ? file : URL.createObjectURL(file);
    });
  }

  async startImageUploadOperations(
    fileInput: UploadFile,
    sessionId: string,
    frameUid: string,
    options?: UploadOptions,
  ): Promise<void> {
    const file = fileInput.file;
    if (!file) {
      return;
    }

    const { width, height } = await this.getImageDimensions(file);

    const boardItem = BoardItemsManager.instance?.insertNewBoardItem<ItemModel.Image>(
      ItemModel.Image,
      {
        width,
        height,
        boardId: frameUid,
        top: options?.y ? options?.y - height / 2 : undefined,
        left: options?.x ? options?.x - width / 2 : undefined,
      },
      { renderInNearestFreeSpace: options?.renderInNearestFreeSpace },
      // We don't want to sync it with other users until the content is loaded, that's why inserting it to canvas only
      true,
    );

    if (!boardItem) {
      return;
    }

    const uploadId = boardItem.uid;

    if (typeof file === 'string') {
      BoardItemsManager.instance?.setOfflineBoardItemStateAndSyncItWithTheServer<ItemModel.Image>(
        boardItem,
        { url: file },
      );
      try {
        await this.uploadToResourceLibrary(
          file,
          fileInput.name,
          fileInput.size,
          fileInput.type,
          fileInput.options,
        );
      } catch (error) {
        boardItem.deleteBoardItem();
        captureException(error);
        this.uploadFileService.cancelUpload(uploadId);
        this.uploadFileService.showUploadFailedNotification(
          UploadFailedNotificationStatus.SYSTEM_FAILURE,
        );
      }
      return;
    }

    const task = this.uploadFileService.uploadToFireStorage(
      file,
      async (url, fileName) => {
        try {
          if (!fileName) {
            return;
          }
          // We convert the image if needed, then resize it, then we set the get the final image dimensions and
          // finally we upload the image to the resource library
          const imageData = await this.getConvertedImageUrlIfNeeded(
            file,
            url,
            fileName,
            boardItem,
            uploadId,
          );

          if (imageData === undefined) {
            // getting an undefined means we need to abort the upload
            return;
          }

          const finalUrl = await this.getResizedImageUrlIfNeeded(
            file,
            imageData.displayUrl,
            fileName,
            imageData.imageConverted,
          );

          const setSuccessfully = await this.setImageConfigAfterConversion(
            boardItem,
            finalUrl,
            uploadId,
            imageData.imageConverted,
          );

          if (!setSuccessfully) {
            return;
          }

          BoardItemsManager.instance?.setOfflineBoardItemStateAndSyncItWithTheServer<ItemModel.Image>(
            boardItem,
            {
              url: finalUrl,
            },
          );

          const contentId = await this.uploadToResourceLibrary(
            finalUrl,
            fileInput.name,
            file.size,
            ResourceItemModel.IMAGE,
            options,
          );

          this.uploadFileService.updateGroupUploadProgress(options);
          this.uploadFileService.onResourceCreated(ResourceItemModel.IMAGE, contentId, uploadId);
        } catch (error) {
          captureException(error);
          this.abortImageUpload(boardItem, uploadId, UploadFailedNotificationStatus.SYSTEM_FAILURE);
        }
      },
      () => {
        boardItem.deleteBoardItem();
      },
    );
    if (task) {
      this.uploadFileService.createUploadTaskObjects(
        uploadId,
        fileInput,
        percentage(task),
        task.cancel,
      );
    } else {
      boardItem.deleteBoardItem();
    }
  }

  startPDFUploadOperations(
    fileInput: UploadFile,
    sessionId: string,
    frameUid: string,
    options?: UploadOptions,
  ): void {
    if (!fileInput.file) {
      return;
    }

    if (fileInput.size / (1024 * 1024) > UPLOAD_LIMITS.DOCUMENT.SIZE) {
      this.uploadFileService.showUploadFailedNotification(
        UploadFailedNotificationStatus.SIZE_LIMIT,
      );
      return;
    }

    // Ensure all files have a valid mime type before upload to Firebase Storage
    const file = resolveDocumentMimeType(fileInput.file as File);

    const boardItem = BoardItemsManager.instance?.insertNewBoardItem<ItemModel.PDF>(
      ItemModel.PDF,
      {
        name: fileInput.name,
        boardId: frameUid,
      },
      { renderInNearestFreeSpace: options?.renderInNearestFreeSpace },
      // We don't want to sync it with other users until the content is loaded, that's why inserting it to canvas only
      true,
    );

    if (!boardItem) {
      return;
    }

    const uploadId = boardItem.uid;

    // TODO <Hatahet> refactor this
    const task = this.uploadFileService.uploadToFireStorage(
      file,
      async (url) => {
        try {
          const { finalPdfUrl, finalPdfSize } = await this.convertToPdfIfNecessary(fileInput, url);

          const contentId = await this.uploadToResourceLibrary(
            finalPdfUrl,
            fileInput.name,
            finalPdfSize,
            ResourceItemModel.DOCUMENT,
            options,
          );

          this.uploadFileService.updateGroupUploadProgress(options);
          this.uploadFileService.onResourceCreated(ResourceItemModel.DOCUMENT, contentId, uploadId);

          BoardItemsManager.instance?.setOfflineBoardItemStateAndSyncItWithTheServer<ItemModel.PDF>(
            boardItem,
            {
              url: finalPdfUrl,
              contentId,
            },
          );
        } catch (error) {
          boardItem.deleteBoardItem();
          captureException(error);
          this.uploadFileService.cancelUpload(uploadId);
          this.uploadFileService.showUploadFailedNotification(
            UploadFailedNotificationStatus.SYSTEM_FAILURE,
          );
        }
      },
      () => {
        boardItem.deleteBoardItem();
      },
    );
    if (task) {
      this.uploadFileService.createUploadTaskObjects(
        uploadId,
        fileInput,
        percentage(task),
        task.cancel,
      );
    } else {
      boardItem.deleteBoardItem();
    }
  }

  async startVideoUploadOperations(
    fileInput: UploadFile,
    sessionId: string,
    frameUid: string,
    options?: UploadOptions,
  ): Promise<void> {
    const file = fileInput.file;
    if (!file) {
      return;
    }

    const boardItem = BoardItemsManager.instance?.insertNewBoardItem<ItemModel.Video>(
      ItemModel.Video,
      {
        title: fileInput.name,
        boardId: frameUid,
        top: options?.y ? options?.y - VIDEO_DEFAULT_HEIGHT / 2 : undefined,
        left: options?.x ? options?.x - VIDEO_DEFAULT_WIDTH / 2 : undefined,
      },
      {},
      // We don't want to sync it with other users until the content is loaded, that's why inserting it to canvas only
      true,
    );

    if (!boardItem) {
      return;
    }

    const uploadId = boardItem.uid;

    if (typeof file === 'string') {
      BoardItemsManager.instance?.setOfflineBoardItemStateAndSyncItWithTheServer<ItemModel.Video>(
        boardItem,
        { url: file },
      );
      try {
        await this.uploadToResourceLibrary(file, fileInput.name, fileInput.size, fileInput.type);
      } catch (error) {
        boardItem.deleteBoardItem();
        captureException(error);
        this.uploadFileService.cancelUpload(uploadId);
        this.uploadFileService.showUploadFailedNotification(
          UploadFailedNotificationStatus.SYSTEM_FAILURE,
        );
      }
      return;
    }

    try {
      const response = await firstValueFrom(this.vimeoStorage.createVideo(file));
      const percentageSubject = new BehaviorSubject({ progress: 0.01 });

      const videoUploadTask = this.vimeoStorage.tusUpload(
        file,
        response.body.upload.upload_link,
        (percentageValue) => {
          percentageSubject.next({ progress: percentageValue });
        },
        () => {
          BoardItemsManager.instance?.setOfflineBoardItemStateAndSyncItWithTheServer<ItemModel.Video>(
            boardItem,
            {
              url: response.body.link,
            },
          );

          this.uploadToResourceLibrary(
            response.body.link,
            fileInput.name,
            fileInput.size,
            ResourceItemModel.VIDEO,
            fileInput.options,
          );
        },
        () => {
          boardItem.deleteBoardItem();
          this.uploadFileService.cancelUpload(uploadId);
          this.uploadFileService.showUploadFailedNotification(
            UploadFailedNotificationStatus.VIMEO_UPLOAD_FAILED,
          );
        },
      );
      if (videoUploadTask) {
        const task = {
          cancel: () => videoUploadTask?.abort,
        };
        this.uploadFileService.createUploadTaskObjects(
          uploadId,
          fileInput,
          percentageSubject.asObservable(),
          task.cancel,
        );
        videoUploadTask.start();
      } else {
        boardItem.deleteBoardItem();
      }
    } catch (error) {
      boardItem.deleteBoardItem();
      this.uploadFileService.showUploadFailedNotification(
        UploadFailedNotificationStatus.VIMEO_UPLOAD_FAILED,
      );
      this.uploadFileService.cancelUpload(uploadId);
    }
  }

  private async convertToPdfIfNecessary(file: UploadFile, uploadedFileUrl: string) {
    if (file.type === ResourceItemModel.PDF || !checkIfValidLibreOfficeType(file.file as File)) {
      return {
        finalPdfUrl: uploadedFileUrl,
        finalPdfSize: file.size,
      };
    }
    const convertedFile = await firstValueFrom(
      this.pdfConverterService.convertToPDF(uploadedFileUrl),
    );
    return {
      finalPdfUrl: convertedFile.url,
      finalPdfSize: convertedFile.size,
    };
  }

  // updated url to match the new one in the storage after resizing the image by firebase extension
  private async generateResizedImageUrl(url: string, fileName?: string) {
    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_RESIZE_IMAGES_EXTENSION)) {
      return url;
    }

    const resizedImageUrlPattern = (environment as any).resizedImageUrlPattern;
    if (!resizedImageUrlPattern || !fileName) {
      return url;
    }

    const width = Number(
      this.flagsService.featureFlagsVariables.enable_resize_images_extension?.max_image_width ??
        DEFAULT_MAX_IMAGE_WIDTH,
    );
    const height = Number(
      this.flagsService.featureFlagsVariables.enable_resize_images_extension?.max_image_height ??
        DEFAULT_MAX_IMAGE_HEIGHT,
    );
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_CDN_RESOURCES_SIGNING)) {
      const resizedPath = `resized/${fileName}_${width}x${height}`;
      try {
        return await this.cdnResourcesSignerService.getSignedUrl(resizedPath);
      } catch (error) {
        // Fall through to Firebase resized URL below
      }
    }

    const urlObj = new URL(url);
    const token = urlObj.searchParams.get('token') ?? '';
    const bucket = environment.firebase.storageBucket;

    return resizedImageUrlPattern
      .replace('{bucket}', bucket)
      .replace('{fileName}', fileName)
      .replace('{width}', String(width))
      .replace('{height}', String(height))
      .replace('{token}', token);
  }

  private isSvgFile(file: File): boolean {
    return file.type === 'image/svg+xml';
  }

  /**
   * Polls the resized image URL to check if it's ready in the bucket
   * @param url - The URL to check
   * @returns Promise that resolves to true if image is ready, false if timeout
   */
  private async waitForResizedImage(url: string): Promise<boolean> {
    const maxAttempts = Number(
      this.flagsService.featureFlagsVariables.enable_resize_images_extension
        ?.max_image_reload_attempts ?? DEFAULT_MAX_IMAGE_RELOAD_ATTEMPTS,
    );
    return pollHeadUntilUrlOk(url, maxAttempts, DEFAULT_IMAGE_RELOAD_POLL_INTERVAL_MS);
  }

  private abortImageUpload(
    boardItem: BoardItem,
    uploadId: string,
    status: UploadFailedNotificationStatus,
  ): void {
    boardItem.deleteBoardItem();
    this.uploadFileService.cancelUpload(uploadId);
    this.uploadFileService.showUploadFailedNotification(status);
  }

  /**
   * build converted WebP URL and wait until it is reachable. Images that don't need conversion returns the Firebase URL unchanged.
   * @returns `undefined` if the conversion could not be prepared (item already torn down).
   */
  private async getConvertedImageUrlIfNeeded(
    file: File,
    firebaseDownloadUrl: string,
    fileName: string | undefined,
    boardItem: BoardItem,
    uploadId: string,
  ): Promise<{ displayUrl: string; imageConverted: boolean } | undefined> {
    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_IMAGES_CONVERSION)) {
      return { displayUrl: firebaseDownloadUrl, imageConverted: false };
    }
    if (!imageNeedsConversion(file)) {
      return { displayUrl: firebaseDownloadUrl, imageConverted: false };
    }

    if (!fileName) {
      this.abortImageUpload(
        boardItem,
        uploadId,
        UploadFailedNotificationStatus.IMAGE_CONVERSION_FAILED,
      );
      return undefined;
    }

    const convertedUrl = await this.imageConverterService.buildConvertedWebpUrl(
      firebaseDownloadUrl,
      fileName,
    );

    const convertedReady = await this.imageConverterService.waitForConvertedWebp(convertedUrl);
    if (!convertedReady) {
      this.abortImageUpload(
        boardItem,
        uploadId,
        UploadFailedNotificationStatus.IMAGE_CONVERSION_FAILED,
      );
      return undefined;
    }

    return { displayUrl: convertedUrl, imageConverted: true };
  }

  /**
   * When the resize extension is enabled (and the file is not SVG / not needs conversion), polls for the resized
   * object URL; otherwise returns `originalUrl`. Falls back to `originalUrl` if the resized asset is not ready in time.
   */
  private async getResizedImageUrlIfNeeded(
    file: File,
    originalUrl: string,
    storageObjectName: string | undefined,
    imageConverted: boolean,
  ): Promise<string> {
    const shouldUseResizedImage =
      !this.isSvgFile(file) &&
      !imageConverted &&
      this.flagsService.isFlagEnabled(FLAGS.ENABLE_RESIZE_IMAGES_EXTENSION);

    if (!shouldUseResizedImage) {
      return originalUrl;
    }

    const resizedUrl = await this.generateResizedImageUrl(originalUrl, storageObjectName);
    const isResizedImageReady = await this.waitForResizedImage(resizedUrl);

    if (isResizedImageReady) {
      return resizedUrl;
    }

    console.warn('Resized image not ready after polling, using original URL');
    return originalUrl;
  }

  /**
   * We need this because for images with certain formats, the browser can't natively know the dimensions before the conversion
   * So now the browser can, we are adjusting the size and position to match the actual ones instead of the assumed one since the lookup for them failed.
   */
  private async setImageConfigAfterConversion(
    boardItem: BoardItem,
    finalUrl: string,
    uploadId: string,
    imageConverted: boolean,
  ): Promise<boolean> {
    if (!imageConverted) {
      return true;
    }
    const initialConfigs = boardItem.configurations;
    const targetArea = initialConfigs.width * initialConfigs.height;
    const dimensions = await this.getImageDimensions(finalUrl);
    if (!dimensions) {
      this.abortImageUpload(
        boardItem,
        uploadId,
        UploadFailedNotificationStatus.IMAGE_CONVERSION_FAILED,
      );
      return false;
    }

    // Since the image is already added with the default width and height, we want to change them to avoid changing the original image aspect ratio
    // But we also won't want the image size to change so we adjust the width and height of the image to match the original area of the image
    const { width, height } =
      this.imageConverterService.transformDimensionsToTargetAreaPreservingAspectRatio(
        dimensions.width,
        dimensions.height,
        targetArea,
      );

    boardItem.configurations.width = width;
    boardItem.configurations.height = height;
    boardItem.configurations.left = initialConfigs.left + (initialConfigs.width - width) / 2;
    boardItem.configurations.top = initialConfigs.top + (initialConfigs.height - height) / 2;

    return true;
  }

  uploadToResourceLibrary(
    url: string,
    name: string,
    size: number,
    type: ResourceItemModel,
    options: any = null,
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (options?.isResourceLibraryObject) {
        // shouldn't create a new resource as it is already exist in our storage
        resolve(uuidv4());
      } else {
        const resource = {
          url: url,
          name: name,
          size: size,
          metadata: {
            topics_ids: [],
            minGrade: 0,
            maxGrade: 10,
          },
          type: type,
          acl: { public: false, visibility: 'CLASS' },
          path: 'Other',
          protected: true,
          uploaded_from_spaces: true,
        };
        if (url.includes('base64')) {
          const errorMessage = 'Corrupted file upload through local files/drag & drop';
          alert(errorMessage);
          console.log(url);
          reject(errorMessage);
        }
        firstValueFrom(this.resourcesService.createResource(resource)).then((res) => {
          if (res?.body) {
            resolve(res.body.resource._id);
          }
        });
      }
    });
  }

  private async insertImageFromUriList(event: DragEvent, position?: CenterPosition) {
    event.preventDefault(); // to not open the image in a new tab

    try {
      const imageFile = await getFileFromUrl(event.dataTransfer?.getData('text/uri-list'));
      imageFile && this.uploadFiles([imageFile], event, position);
    } catch (_) {
      // The reason is probably CORS
      this.showUploadFailedNotification(
        this.translate.instant(
          'Drag-and-drop upload is not supported for this file. Please download it locally and upload it manually.',
        ),
      );
    }
  }

  private showUploadFailedNotification(messageText: string) {
    const title = this.translate.instant('upload is not allowed');
    const icon = {
      assetName: 'lock_outline',
      type: NotificationIconTypes.MAT_ICON,
      size: 16,
      outlined: true,
    };
    this.notificationToasterService.showDefaultErrorNotificationV2(
      title,
      messageText,
      INFOS.SPACE_LOCKED,
      icon,
      undefined,
      8,
    );
  }

  /**
   * Calculates the optimal dimensions for an image based on viewport constraints
   * @param img - The image element to calculate dimensions for
   * @returns Object containing the calculated height and width
   */
  private calculateImageDimensions(img: HTMLImageElement): { height: number; width: number } {
    // We keep the original dimensions of the image unless the bigger than the viewport

    /**
     * Calculate image dimensions that fit within the viewport while accounting for toolbars and zoom.
     *
     * Key concepts:
     * 1. Viewport boundaries (from VPT) are in canvas coordinates - they scale down with zoom
     * 2. Toolbar heights (and padding) are in screen pixels - they don't scale
     * 3. To subtract toolbar heights from canvas coordinates, we must scale them by zoom
     *
     * Example:
     * - At 1x zoom: viewport height = 1000px, toolbar = 80px → available = 920px
     * - At 2x zoom: viewport height = 500px (but visibly the same), toolbar = 80px * 0.5 - 40px → available = 460px
     * - We multiply toolbar by 0.5 (1/zoom) to maintain proper proportions
     */
    const zoom = Math.min(1 / (this.sharedDataService.fabricCanvas?.getZoom() || 1), 1);

    const DEFAULT_BOTTOM_TOOLBAR_HEIGHT = 82;
    // Get toolbar heights adjusted for zoom
    const bottomToolbarHeight =
      zoom *
      (document.getElementById('main-toolbar-v2')?.getBoundingClientRect().height ??
        DEFAULT_BOTTOM_TOOLBAR_HEIGHT);

    const DEFAULT_TOP_TOOLBAR_HEIGHT = 48;
    const topToolbarHeight =
      zoom *
      (document.getElementById('top-nav-bar')?.getBoundingClientRect().height ??
        DEFAULT_TOP_TOOLBAR_HEIGHT);

    // Get viewport boundaries
    const viewport = this.sessionsVptService.getViewPortBoundingRect();
    if (!viewport) {
      return { height: img.height, width: img.width };
    }

    // Calculate available space in viewport
    const PADDING_FROM_SPACE_BOUNDARIES = 80;
    const padding = PADDING_FROM_SPACE_BOUNDARIES * zoom;
    const availableHeight =
      viewport.bottom - viewport.top - bottomToolbarHeight - topToolbarHeight - padding;
    const availableWidth = viewport.right - viewport.left - padding;

    // If image fits within viewport, return original dimensions
    if (availableHeight >= img.height && availableWidth >= img.width) {
      return { height: img.height, width: img.width };
    }

    // Calculate scale to fit image within viewport
    const widthScale = availableWidth / img.width;
    const heightScale = availableHeight / img.height;
    const scale = Math.min(widthScale, heightScale);

    return {
      height: Math.floor(img.height * scale),
      width: Math.floor(img.width * scale),
    };
  }
}
